import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';
import isEqual from 'lodash/isEqual';
import { useState, useEffect } from 'react';

import { IConstructorFacetMultiple } from 'shared/types/ConstructorFacet';
import { LocalizedCurrency } from 'shared/components/LocalizedCurrency';
import {
  getPriceRangeFromBucket,
  shouldShowPlusSign,
  transformArrayToPriceBucketValue,
  transformBucketValueToRange,
} from 'search/utils/mobilePriceBucketHelpers';
import { removeDoubleQuotes } from 'search/utils/removeDoubleQuotes';
import { SearchBoxButton } from 'search/styles/SearchStyles';
import { t } from 'localization/utils/t';

const TP = 'search.components.searchGrid.SearchFilterPriceBucketMobile';

const Slider = dynamic(() => import('shared/components/Slider').then((mod) => mod.Slider), {
  ssr: false,
});

interface ISearchFilterPriceBucketMobile {
  facet: IConstructorFacetMultiple;
  selectedFilterValues: Array<string>;
  setSelectedFilterValues: (selectedFilterValues: Array<string> | string) => void;
}

export const SearchFilterPriceBucketMobile = ({
  facet,
  selectedFilterValues,
  setSelectedFilterValues,
}: ISearchFilterPriceBucketMobile) => {
  // values coming back from Constructor are wrapped in double quotes (eg. '"100"-"9900"')
  const options = facet?.options?.map((option) => ({
    ...option,
    value: removeDoubleQuotes(option.value),
  }));
  const formattedSelectedFilterValues = (
    Array.isArray(selectedFilterValues) ? selectedFilterValues : [selectedFilterValues]
  )?.map((val) => removeDoubleQuotes(val));

  const range: [number, number] = getPriceRangeFromBucket(options);
  const initialState = transformBucketValueToRange(formattedSelectedFilterValues);
  const [values, setValues] = useState(initialState || range);
  const [update, setUpdate] = useState(initialState || range);
  const areMultipleRangesSelected = formattedSelectedFilterValues.length > 1;

  const handleSlideEnd = (updatedValues: [number, number]) => {
    setValues(updatedValues);
    const bucketValue = transformArrayToPriceBucketValue(updatedValues);
    setSelectedFilterValues(bucketValue);
  };

  const handleSlideUpdate = (updatedValues) => {
    setUpdate(updatedValues);
  };

  const forceSliderUpdate = (updatedValues) => {
    setValues(updatedValues);
    setUpdate(updatedValues);
  };

  const handleOnChange = (optionValue) => {
    // We filter out options that do not have a count greater than 0, because that means
    // it is not a default bucket provided by Constructor. It is actually a price range we
    // have set, and we need to remove it when we select a quick filter bucket
    const filteredOutRangeSelection = formattedSelectedFilterValues.filter((val) => {
      const currentOption = options.find((option) => option.value === val);
      return currentOption?.count > 0;
    });

    const isOptionAlreadySelected = filteredOutRangeSelection?.includes(optionValue);
    if (isOptionAlreadySelected) {
      const updatedValues = filteredOutRangeSelection.filter((value) => {
        return value !== optionValue;
      });
      setSelectedFilterValues(updatedValues);
      // if only 1 bucket is selected, move the sliders to that bucket range
      const isOnlyOneYearBucketSelected = updatedValues?.length === 1;
      if (isOnlyOneYearBucketSelected) {
        forceSliderUpdate(transformBucketValueToRange(updatedValues));
      }
      // when no buckets are selected, reset sliders back to initial range
      if (updatedValues?.length === 0) {
        forceSliderUpdate(range);
      }
    } else {
      const updatedValues = [...filteredOutRangeSelection, optionValue];
      setSelectedFilterValues(updatedValues);
      // if only 1 bucket is selected, move the sliders to that bucket range
      const isOnlyOneYearBucketSelected = updatedValues?.length === 1;
      if (isOnlyOneYearBucketSelected) {
        forceSliderUpdate(transformBucketValueToRange(updatedValues));
      }
    }
  };

  useEffect(() => {
    // BUY3-2341: when we switch from a different filter (ex: size, condition, etc.)
    // and the state has not updated until the 2nd re-render,
    // we need to force the sliders to update when the selectedFilterValues update
    if (selectedFilterValues.length > 0 && !isEqual(values, initialState)) {
      forceSliderUpdate(initialState);
    }
  }, [selectedFilterValues]);

  return (
    <>
      {areMultipleRangesSelected ? (
        <SearchFilterPriceBucketMobile.MultipleRangesSelected data-qa="multiple_ranges_selected">
          {t(`${TP}.multipleRangesSelected`, 'Multiple Ranges Selected')}
        </SearchFilterPriceBucketMobile.MultipleRangesSelected>
      ) : (
        <SearchFilterPriceBucketMobile.YearSliderKeys>
          <span data-qa="price_slider_min_value">
            <LocalizedCurrency price={update?.[0]} />
          </span>
          <span data-qa="price_slider_max_value">
            <LocalizedCurrency price={update?.[1]} />
            {shouldShowPlusSign(formattedSelectedFilterValues) && '+'}
          </span>
        </SearchFilterPriceBucketMobile.YearSliderKeys>
      )}
      <SearchFilterPriceBucketMobile.SliderWrapper>
        {areMultipleRangesSelected ? (
          <SearchFilterPriceBucketMobile.Line />
        ) : (
          <Slider
            data-qa="price_filter_slider"
            handleSlideEnd={handleSlideEnd}
            handleUpdate={handleSlideUpdate}
            range={range}
            step={100}
            values={values}
          />
        )}
      </SearchFilterPriceBucketMobile.SliderWrapper>
      <div>
        {options?.map((option) => {
          if (option.count < 1) {
            return;
          }
          return (
            <SearchFilterPriceBucketMobile.SearchBoxButton
              data-qa="mobile_filter_price_button"
              key={option.value}
              onClick={() => handleOnChange(option.value)}
              $isActive={formattedSelectedFilterValues?.includes(option.value)}
            >
              {option.display_name}
            </SearchFilterPriceBucketMobile.SearchBoxButton>
          );
        })}
      </div>
    </>
  );
};

SearchFilterPriceBucketMobile.SearchBoxButton = styled(SearchBoxButton)<{ $isActive: boolean }>`
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  border: 1px solid ${({ theme }) => theme.navigation.borderColor};
  color: ${({ theme }) => theme.navigation.textColor};
  ${({ $isActive }) =>
    $isActive &&
    css`
      color: ${({ theme }) => theme.navigation.backgroundColor};
      background-color: ${({ theme }) => theme.navigation.loaderColor};
    `}
  &:hover {
    text-decoration: none;
  }
`;

SearchFilterPriceBucketMobile.Line = styled.hr`
  background-color: ${({ theme }) => theme.colors.black};
  opacity: 1;
`;

SearchFilterPriceBucketMobile.MultipleRangesSelected = styled.div`
  text-align: center;
`;

SearchFilterPriceBucketMobile.YearSliderKeys = styled.div`
  display: flex;
  justify-content: space-between;
`;

SearchFilterPriceBucketMobile.SliderWrapper = styled.div`
  margin: 24px 8px;
`;
